// Unpublished Work © 2020-2025 Deere & Company.

import React from 'react';
import {setUserData} from '../action-creators/user-data';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {LoadingContainer} from '../views/common/loading-indicator/loading-container';
import {PageStatus} from '../state/struct/page-status';
import {useOktaAuth} from '@okta/okta-react';
import {useHistory} from 'react-router-dom';
import {CustomerRoutes} from '../literals/customer-routes';
import {REDIRECT_USER_TO_PROFILE_SCREEN, REDIRECT_USER_TO_SEARCH_SCREEN, SET_CONFIGURATION} from '../action-list';
import {useIntl} from 'react-intl';
import {logUserEntryOnce} from '../repositories/utils/log-user-entry';

const useUserEntrySession = (applicationName, userId) => {
    React.useEffect(() => {
        logUserEntryOnce(applicationName, userId);
    }, [applicationName, userId]);
};

// eslint-disable-next-line max-statements
export const UserDataProvider = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const intl = useIntl();

    const businessActivities = useSelector((state) => state.configuration.businessActivities);
    const applicationName = useSelector((state) => state.configuration.parentAppName);
    const userId = useSelector((state) => state.user?.loggedInUserId);
    const {authState} = useOktaAuth();

    const [pageStatus, setPageStatus] = React.useState(PageStatus.meta.map.LOADING);
    const [errorMessage, setErrorMessage] = React.useState('');
    const routeRequested = useSelector((state) => state.configuration.routing.view);
    const returnLabel = intl.formatMessage({
        id: 'labels.BACK'
    });
    const isUserAccessingEditPageWithoutAccess = (routeRequested === 'EDIT_CUSTOMER' || routeRequested === 'ADD_BUSINESS_CONTACT') && businessActivities.canAddEdit === false;

    const checkIfUserIsAccessingEditWithoutPermissions = () => {
        if (isUserAccessingEditPageWithoutAccess) {
            history.push(CustomerRoutes.CUSTOMER_PROFILE);
            dispatch({
                configuration: {
                    features: {
                        search: {
                            addToContextButton: {
                                show: true,
                                tooltipLabel: returnLabel,
                                label: returnLabel
                            }
                        }
                    },
                    routing: {
                        view: 'CUSTOMER_PROFILE'
                    }
                },
                type: SET_CONFIGURATION
            });
            dispatch({
                isUserRedirectedToProfile: true,
                type: REDIRECT_USER_TO_PROFILE_SCREEN
            });
        }
    };

    const checkIfUserIsAccessingAddPageWithoutPermissions = () => {
        if (routeRequested === 'ADD_CUSTOMER' && businessActivities.canAddEdit === false) {
            history.push(CustomerRoutes.CUSTOMER_SEARCH);
            dispatch({
                configuration: {
                    routing: {
                        view: 'CUSTOMER_SEARCH'
                    }
                },
                type: SET_CONFIGURATION
            });
            dispatch({
                isUserRedirectedToSearch: true,
                type: REDIRECT_USER_TO_SEARCH_SCREEN
            });
        }
    };

    async function loadUser() {
        try {
            await dispatch(setUserData(businessActivities, authState.accessToken));

            setPageStatus(PageStatus.meta.map.COMPLETE);
        } catch (e) {
            setPageStatus(PageStatus.meta.map.ERROR);
            setErrorMessage(e.errorUserMessage);
        }
    }

    React.useEffect(() => {
        loadUser();
    }, [authState.accessToken]);

    React.useEffect(() => {
        checkIfUserIsAccessingEditWithoutPermissions();
        checkIfUserIsAccessingAddPageWithoutPermissions();
    }, [businessActivities.canAddEdit]);

    useUserEntrySession(applicationName, userId);

    return (
        <LoadingContainer
            loadingStatus={pageStatus}
            message={errorMessage}
        >
            {props.children}
        </LoadingContainer>
    );
};

UserDataProvider.propTypes = {
    children: PropTypes.node
};
